import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Upload, Button, Typography, Space, Progress, Table, Alert, Row, Col, Tag, message, Checkbox, Popconfirm, Modal, Radio } from 'antd';
import { InboxOutlined, ReloadOutlined, CloudUploadOutlined, StopOutlined, LoadingOutlined } from '@ant-design/icons';
import ModuleNav from './ModuleNav';
import { createZipImportJob, getZipImportJob, listZipImportJobs, cancelZipImportJob } from '../../services/aiImageMaskingApi';

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;

const statusColor = {
  queued: 'blue',
  running: 'processing',
  completed: 'success',
  failed: 'error',
};

const ZipImport = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [history, setHistory] = useState([]);
  const [, setPolling] = useState(false);
  const [cleanupOnCancel, setCleanupOnCancel] = useState(false);
  const [duplicateStrategy, setDuplicateStrategy] = useState('overwrite');
  const [detailRecord, setDetailRecord] = useState(null);

  const progressPercent = useMemo(() => {
    if (!jobDetails || !jobDetails.total_files) return 0;
    return Math.min(100, Math.round((jobDetails.processed_files / jobDetails.total_files) * 100));
  }, [jobDetails]);

  const loadHistory = useCallback(async () => {
    try {
      const data = await listZipImportJobs({ limit: 10 });
      setHistory(data?.items || []);
    } catch (err) {
      message.error(err.message || 'Unable to load import history');
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Restore last job on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem('ai_zip_import_job');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.job_id) {
          setJob(parsed);
          getZipImportJob(parsed.job_id)
            .then((data) => setJobDetails(data))
            .catch(() => {});
        }
      }
    } catch (err) {
      // ignore
    }
  }, []);

  const pollJob = useCallback(
    (jobId) => {
      if (!jobId) return;
      setPolling(true);
      const interval = setInterval(async () => {
        try {
          const data = await getZipImportJob(jobId);
          setJobDetails(data);
          if (['completed', 'failed'].includes(data.status)) {
            clearInterval(interval);
            setPolling(false);
            loadHistory();
            localStorage.removeItem('ai_zip_import_job');
          }
        } catch (err) {
          clearInterval(interval);
          setPolling(false);
          message.error(err.message || 'Unable to fetch job status');
        }
      }, 3000);
      return () => clearInterval(interval);
    },
    [loadHistory],
  );

  useEffect(() => {
    if (job?.job_id) {
      const stop = pollJob(job.job_id);
      return () => {
        if (stop) stop();
      };
    }
    return undefined;
  }, [job, pollJob]);

  const handleUpload = async () => {
    if (!file) {
      message.warning('Select a ZIP file to upload');
      return;
    }
    setUploading(true);
    try {
      const res = await createZipImportJob(file, duplicateStrategy);
      setJob(res);
      setJobDetails({ ...res, files: [], processed_files: 0, total_files: 0 });
      localStorage.setItem('ai_zip_import_job', JSON.stringify(res));
      message.success('Import started');
    } catch (err) {
      message.error(err.message || 'Unable to start import');
    } finally {
      setUploading(false);
    }
  };

  const stageTag = (stage, state) => {
    const colorMap = { completed: 'success', running: 'processing', pending: 'default', failed: 'error' };
    const text = stage;
    const icon = state === 'running' ? <LoadingOutlined spin /> : null;
    return (
      <Tag color={colorMap[state]} icon={icon}>
        {text}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'File',
      dataIndex: 'filename',
      key: 'filename',
      render: (text) => <Text ellipsis>{text}</Text>,
    },
    {
      title: 'Size',
      dataIndex: 'size_bytes',
      key: 'size_bytes',
      render: (value) => (value ? `${(value / 1024).toFixed(1)} KB` : '—'),
    },
    {
      title: 'Stages',
      key: 'stages',
      render: (_, record) => {
        const status = (record.status || 'pending').toLowerCase();
        const message = record.message || '';
        const meta = record.metadata || {};
        const hasMarkdown = message.includes('md:') || record.blob_url?.endsWith('.md');
        const mdError = message.includes('md_error');
        const vectorPending = (record.metadata && record.metadata.vector_pending) || message.includes('vector_pending');
        const vectorError =
          (record.metadata && record.metadata.vector_error) ||
          (message.match(/vector_error:\s*([^;]+)/) || [])[1] ||
          (message.includes('vector_error') ? message : null);
        const stepLogs = meta.step_logs || [];

        const extractState =
          status === 'failed'
            ? 'failed'
            : status === 'running'
            ? 'running'
            : status === 'completed'
            ? 'completed'
            : 'pending';
        const mdState =
          status === 'failed' || mdError
            ? 'failed'
            : hasMarkdown
            ? 'completed'
            : status === 'running'
            ? 'running'
            : 'pending';
        const vecState =
          vectorError
            ? 'failed'
            : vectorPending
            ? 'pending'
            : status === 'completed'
            ? 'completed'
            : status === 'running'
            ? 'running'
            : 'pending';

        return (
          <Space size={6} wrap>
            <Space direction="vertical" size={0}>
              {stageTag('Zip Extract', extractState)}
              {stepLogs
                .filter((l) => l.stage === 'extract')
                .map((l) => (
                  <Text key={l.msg} type="secondary" style={{ fontSize: 11 }}>
                    {l.msg}
                  </Text>
                ))}
            </Space>
            <Space direction="vertical" size={0}>
              {stageTag('Data Extraction', mdState)}
              {stepLogs
                .filter((l) => l.stage === 'markdown')
                .map((l) => (
                  <Text key={l.msg} type="secondary" style={{ fontSize: 11 }}>
                    {l.msg}
                  </Text>
                ))}
            </Space>
            <Space direction="vertical" size={0}>
              {stageTag('Vector Conversion', vecState)}
              {stepLogs
                .filter((l) => l.stage === 'vector')
                .map((l) => (
                  <Text key={l.msg} type="secondary" style={{ fontSize: 11 }}>
                    {l.msg}
                  </Text>
                ))}
              {vectorError && (
                <Text type="danger" style={{ fontSize: 11 }}>
                  {vectorError}
                </Text>
              )}
            </Space>
          </Space>
        );
      },
    },
    {
      title: 'Blob',
      dataIndex: 'blob_url',
      key: 'blob_url',
      render: (value) =>
        value ? (
          <a href={value} target="_blank" rel="noopener noreferrer">
            Open
          </a>
        ) : (
          '—'
        ),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      render: (text) => (text ? <Text type="secondary">{text}</Text> : '—'),
    },
    {
      title: 'Message / Error',
      key: 'message',
      render: (_, record) => {
        const meta = record.metadata || {};
        const vectorError =
          meta.vector_error ||
          (record.message && (record.message.match(/vector_error:\s*([^;]+)/) || [])[1]) ||
          (record.message && record.message.includes('vector_error') ? record.message : null);
        const msgs = [record.message, vectorError].filter(Boolean);
        if (!msgs.length) return '—';
        return (
          <Space direction="vertical" size={2}>
            {msgs.map((m, idx) => (
              <Text key={`${record.filename}-msg-${idx}`} type={idx === 0 ? 'secondary' : 'danger'}>
                {m}
              </Text>
            ))}
          </Space>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <Button size="small" onClick={() => setDetailRecord(record)}>
          Details
        </Button>
      ),
    },
  ];

  return (
    <div className="ai-image-masking-page">
      <ModuleNav activeKey="zip-import" />

      <Card className="shadow-sm mb-4" bodyStyle={{ padding: 18 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={16}>
            <Space direction="vertical" size={4}>
              <Title level={4} style={{ margin: 0 }}>
                Bulk ZIP Import
              </Title>
              <Text type="secondary">
                Upload a ZIP archive; files will be extracted, pushed to blob storage, and recorded in the database.
              </Text>
            </Space>
          </Col>
          <Col xs={24} md={8} className="d-flex justify-content-md-end justify-content-start">
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadHistory}>
                Refresh history
              </Button>
              <Button type="primary" icon={<CloudUploadOutlined />} loading={uploading} onClick={handleUpload}>
                Start import
              </Button>
              {job?.job_id && (
                <Popconfirm
                  title="Stop this job?"
                  description="Cancel processing. Optionally delete batch artifacts if cleanup is checked."
                  okText="Stop"
                  cancelText="Keep running"
                  onConfirm={async () => {
                    try {
                      await cancelZipImportJob(job.job_id, { cleanup: cleanupOnCancel });
                      message.success('Job cancelled');
                      localStorage.removeItem('ai_zip_import_job');
                      setPolling(false);
                      setJobDetails((prev) => (prev ? { ...prev, status: 'cancelled', message: 'Cancelled' } : prev));
                    } catch (err) {
                      message.error(err.message || 'Unable to cancel job');
                    }
                  }}
                >
                  <Button danger icon={<StopOutlined />}>Stop job</Button>
                </Popconfirm>
              )}
              <Checkbox checked={cleanupOnCancel} onChange={(e) => setCleanupOnCancel(e.target.checked)}>
                Cleanup
              </Checkbox>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card className="shadow-sm h-100" title="Upload ZIP" bodyStyle={{ padding: 18 }}>
            <Dragger
              multiple={false}
              accept=".zip"
              maxCount={1}
              beforeUpload={(f) => {
                setFile(f);
                return false;
              }}
              onRemove={() => setFile(null)}
              fileList={file ? [file] : []}
              height={240}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag ZIP file to this area to upload</p>
              <p className="ant-upload-hint">Only .zip archives are allowed</p>
            </Dragger>
            <Paragraph className="mt-3 mb-0">
              Selected: <Text strong>{file ? file.name : 'None'}</Text>
            </Paragraph>
            <Space className="mt-2" direction="vertical">
              <Text strong className="mb-1">
                Duplicate files
              </Text>
              <Radio.Group
                value={duplicateStrategy}
                onChange={(e) => setDuplicateStrategy(e.target.value)}
                optionType="button"
                buttonStyle="solid"
                size="small"
              >
                <Radio value="overwrite">Overwrite</Radio>
                <Radio value="skip">Skip</Radio>
              </Radio.Group>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card className="shadow-sm h-100" title="Job status" bodyStyle={{ padding: 18 }}>
            {jobDetails ? (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Space wrap>
                  <Text strong>Job ID:</Text>
                  <Text code>{jobDetails.job_id}</Text>
                </Space>
                <Space wrap>
                  <Text strong>Filename:</Text>
                  <Text>{jobDetails.filename}</Text>
                </Space>
                <Space wrap>
                  <Text strong>Status:</Text>
                  <Tag color={statusColor[jobDetails.status] || 'default'}>{jobDetails.status}</Tag>
                  {jobDetails.message && <Text type="secondary">{jobDetails.message}</Text>}
                </Space>
                {jobDetails.message && (
                  <Alert message={jobDetails.message} type="info" showIcon banner />
                )}
                <Progress
                  percent={progressPercent}
                  status={jobDetails.status === 'failed' ? 'exception' : progressPercent === 100 ? 'success' : 'active'}
                />
                <Table
                  size="small"
                  rowKey="file_id"
                  columns={columns}
                  dataSource={jobDetails.files || []}
                  pagination={false}
                  scroll={{ y: 220 }}
                />
              </Space>
            ) : (
              <Alert message="No job selected. Upload a ZIP to start an import." type="info" showIcon />
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        open={!!detailRecord}
        title={detailRecord ? detailRecord.filename : 'File details'}
        onCancel={() => setDetailRecord(null)}
        footer={null}
      >
        {detailRecord && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Text strong>Status:</Text>
              <Tag color={statusColor[detailRecord.status] || 'default'}>{detailRecord.status}</Tag>
            </Space>
            {detailRecord.message && (
              <Alert message={detailRecord.message} type="info" showIcon />
            )}
            {(() => {
              const metaErr = detailRecord.metadata?.vector_error;
              const msgErr =
                detailRecord.message &&
                ((detailRecord.message.match(/vector_error:\s*([^;]+)/) || [])[1] ||
                  (detailRecord.message.includes('vector_error') ? detailRecord.message : null));
              if (metaErr || msgErr) {
                return <Alert message={metaErr || msgErr} type="error" showIcon />;
              }
              return null;
            })()}
            {detailRecord.metadata?.step_logs?.length ? (
              <Card size="small" title="Step log">
                <Space direction="vertical" style={{ width: '100%' }}>
                  {detailRecord.metadata.step_logs.map((log, idx) => (
                    <div key={`${detailRecord.filename}-log-${idx}`}>
                      <Text strong>{log.stage}: </Text>
                      <Text type="secondary">{log.msg}</Text>
                    </div>
                  ))}
                </Space>
              </Card>
            ) : null}
          </Space>
        )}
      </Modal>

      <Card className="shadow-sm mt-4" title="Recent imports" bodyStyle={{ padding: 18 }}>
        <Table
          rowKey="job_id"
          dataSource={history}
          pagination={false}
          columns={[
            { title: 'Job', dataIndex: 'job_id', key: 'job_id', render: (text) => <Text code>{text}</Text> },
            { title: 'File', dataIndex: 'filename', key: 'filename', render: (text) => <Text>{text}</Text> },
            {
              title: 'Progress',
              key: 'progress',
              render: (_, record) => (
                <Space size={6}>
                  <Progress
                    percent={
                      record.total_files
                        ? Math.round((record.processed_files / record.total_files) * 100)
                        : record.status === 'completed'
                        ? 100
                        : 0
                    }
                    size="small"
                    style={{ minWidth: 120 }}
                    status={record.status === 'failed' ? 'exception' : 'active'}
                  />
                  <Text type="secondary">
                    {record.processed_files}/{record.total_files}
                  </Text>
                </Space>
              ),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              render: (value) => <Tag color={statusColor[value] || 'default'}>{value}</Tag>,
            },
            {
              title: '',
              key: 'action',
              render: (_, record) => (
                <Button
                  size="small"
                  onClick={async () => {
                    setJob({ job_id: record.job_id });
                    localStorage.setItem('ai_zip_import_job', JSON.stringify({ job_id: record.job_id }));
                    try {
                      const data = await getZipImportJob(record.job_id);
                      setJobDetails(data);
                    } catch (err) {
                      message.error(err.message || 'Unable to load job');
                    }
                  }}
                >
                  View
                </Button>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default ZipImport;

